import { Metamodel } from 'app/model/base/model';
import { DataFlowDatasetSettings, DatasetFormatType } from './dataflow-dataset-settings';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { format, IViolation } from 'shell-models-lib';
import { DataFlowExpression } from '../dataflowexpression';
import { EventHubsDataset } from 'app/model/dataset/event-hubs.dataset';
import { BooleanDataFlowType, NumberDataFlowType, StringDataFlowType, TimeStampDataFlowType } from '../dataflow-datatype';
import { DataFlowSource } from '../dataflow-source';
import { isTruthy } from '../dataflow-expression-utilities';

const supportedMessageFormat = new Set(['passthrough', 'json', 'avro', 'delimited']);
export enum streamingRunMode {
    Beginning,
    Timestamp,
    Now
}
@Metamodel.model({
    name: adfModelTypeMap.eventHubs.dataflowSettings,
    validator: EventHubsSettings.validateSettings
})
export class EventHubsSettings extends DataFlowDatasetSettings {
    public static validateSettings(settings: EventHubsSettings): IViolation[] {
        const violations: IViolation[] = [];

        if (!settings.eventHubsNamePattern?.expression && !settings.eventHubsName?.expression) {
            violations.push({
                context: settings.transform,
                message: () => format(stringResources.RequiredTemplate, (settings.owner instanceof DataFlowSource) ? stringResources.EventHubs_AddEventHubs_DisplayText : stringResources.AddTargetEventHub)
            });
        }
        if (!settings.format) {
            violations.push({
                context: settings.transform,
                message: () => format(stringResources.RequiredTemplate, stringResources.DataFormat_Label)
            });
        }
        else if (!supportedMessageFormat.has(settings.format.expression)) {
            violations.push({
                context: settings.transform,
                message: () => stringResources.Streaming_Format_Validation
            });
        }
        if (settings.format?.expression === 'avro' && !settings.payloadSchema?.expression) {
            violations.push({
                context: settings.transform,
                message: () => stringResources.Streaming_Avro_Schema_Validation
            });
        }
        if (settings.format?.expression !== 'passthrough' && !isTruthy(settings.isAutoInfer) && !settings.payloadSchema?.expression) {
            violations.push({
                context: settings.transform,
                message: () => format(stringResources.Streaming_PayloadSchema_Validation)
            });
        }
        if (isTruthy(settings.isAutoInfer) && (!settings.numSampleRecords?.expression || Number(settings.numSampleRecords.expression) < 1 || Number(settings.numSampleRecords.expression) > 2147483647)) {
            violations.push({
                context: settings.transform,
                message: () => stringResources.Streaming_NumSampleRecords_Validation
            });
        }
        return violations;
    }

    public getDatasetType(): string {
        return Metamodel.modelFor(EventHubsDataset).name;
    }

    public getSubFormat(): string {
        return undefined;
    }

    public getFormat(): DatasetFormatType {
        return DatasetFormatType.EventHubs;
    }

    public getIconClass(): string {
        return 'i-100x100-event_hubs';
    }

    public rowDelimiter: string;

    @Metamodel.attribute({
    })
    public includeHeaders: DataFlowExpression;
    @Metamodel.attribute({
    })
    public changeDataCapture: DataFlowExpression;
    @Metamodel.attribute({
    })
    public seekToTimestamp: DataFlowExpression;
    public runMode: streamingRunMode = streamingRunMode.Beginning;

    @Metamodel.attribute({
    })
    public format: DataFlowExpression;

    @Metamodel.attribute({
    })
    public eventHubsNamePattern?: DataFlowExpression;

    @Metamodel.attribute({
    })
    public eventHubsName?: DataFlowExpression;

    @Metamodel.attribute({
    })
    public instanceSelectionType?: DataFlowExpression;

    @Metamodel.attribute({
    })
    public payloadSchema: DataFlowExpression;

    @Metamodel.attribute({
    })
    public store: string;

    @Metamodel.attribute({
    })
    public namespaceName: string;

    @Metamodel.attribute({
    })
    public accessKey: string;

    @Metamodel.attribute({
    })
    public accessKeyName: string;

    @Metamodel.attribute({
    })
    public saslMechanism: string;

    @Metamodel.attribute({
    })
    public securityProtocol: string;

    @Metamodel.attribute({
    })
    public isAutoInfer?: DataFlowExpression;

    @Metamodel.attribute({
    })
    public numSampleRecords?: DataFlowExpression;

    @Metamodel.attribute({
    })
    public authenticationType: string;

    @Metamodel.attribute({
    })
    public isSinkSchemaQuery: boolean = false;
    @Metamodel.attribute({
    })
    public skipInitialLoad: DataFlowExpression;

    public initialize(subformat: string): void {
        this.format = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, 'passthrough');
        this.instanceSelectionType = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, 'manual');
        this.eventHubsNamePattern = this.transform.createTemplatableExpression();
        this.eventHubsName = this.transform.createTemplatableExpression();
        this.payloadSchema = this.transform.createTemplatableExpression();
        this.includeHeaders = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.changeDataCapture = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
        this.seekToTimestamp = this.transform.createTemplatableExpression(TimeStampDataFlowType.TYPE);
        this.isAutoInfer = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
        this.numSampleRecords = this.transform.createTemplatableExpression(NumberDataFlowType.TYPE, '10000');
        this.skipInitialLoad = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
    }
}
